import React, { useState, useEffect, useCallback } from 'react';
import './Play.scss';

interface PlayProps {
  currentLevel: number;
  currentSong: number;
  currentLanguage: string;
  translate: boolean;
  examples: Example[];
  top10Songs: Song[];
  onNext?: () => void;
  onPrevious?: () => void;
}

interface Example {
  id: string;
  level: number;
  songId: string;
  imageFull: string;
  imageUrl: string;
  index: number;
  twisted: string[];
  text: string;
  imagePermanentLink: string;
  translation: Record<string, string>;
}

interface Song {
  id: string;
  name: string;
  artist: string;
  position: number;
  yt: string[];
  ytIndex: number;
  translation: Record<string, string>;
}

interface Card {
  id: string;
  pairId: string;
  imageUrl: string;
  text: string;
  isFlipped: boolean;
  isMatched: boolean;
}

const Play: React.FC<PlayProps> = ({ currentLevel, currentSong, currentLanguage, translate, examples, top10Songs, onNext, onPrevious }) => {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [celebrating, setCelebrating] = useState<boolean>(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Function to speak text in English
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Initialize game with random cards
  const initializeGame = useCallback(() => {
    const numberOfPairs = 6; // 6 pairs = 12 cards
    const shuffledExamples = [...examples]
      .sort(() => Math.random() - 0.5)
      .slice(0, numberOfPairs);

    const gameCards: Card[] = [];

    shuffledExamples.forEach((example) => {
      // First card of the pair
      gameCards.push({
        id: `${example.id}-1`,
        pairId: example.id,
        imageUrl: example.imageUrl,
        text: example.text,
        isFlipped: false,
        isMatched: false,
      });

      // Second card of the pair (same image)
      gameCards.push({
        id: `${example.id}-2`,
        pairId: example.id,
        imageUrl: example.imageUrl,
        text: example.text,
        isFlipped: false,
        isMatched: false,
      });
    });

    // Shuffle all cards
    const shuffledCards = gameCards.sort(() => Math.random() - 0.5);
    setCards(shuffledCards);
    setFlippedCards([]);
    setCelebrating(false);
  }, [examples]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && onNext) {
        onNext();
      } else if (e.key === 'ArrowLeft' && onPrevious) {
        onPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrevious]);

  useEffect(() => {
    if (examples && examples.length > 0) {
      initializeGame();
    }
  }, [examples, initializeGame]);

  // Handle card click
  const handleCardClick = (index: number) => {
    if (isChecking || flippedCards.length >= 2 || cards[index].isFlipped || cards[index].isMatched) {
      return;
    }

    // Speak the English text
    speakText(cards[index].text);

    const newCards = [...cards];
    newCards[index].isFlipped = true;
    setCards(newCards);

    const newFlippedCards = [...flippedCards, index];
    setFlippedCards(newFlippedCards);

    if (newFlippedCards.length === 2) {
      setIsChecking(true);

      const [firstIndex, secondIndex] = newFlippedCards;
      const firstCard = newCards[firstIndex];
      const secondCard = newCards[secondIndex];

      if (firstCard.pairId === secondCard.pairId) {
        // Match found!
        setTimeout(() => {
          newCards[firstIndex].isMatched = true;
          newCards[secondIndex].isMatched = true;
          setCards(newCards);
          setFlippedCards([]);
          setIsChecking(false);

          // Check if all pairs are matched
          const allMatched = newCards.every(card => card.isMatched);
          if (allMatched) {
            setCelebrating(true);
            setTimeout(() => {
              initializeGame();
            }, 3000);
          }
        }, 800);
      } else {
        // No match
        setTimeout(() => {
          newCards[firstIndex].isFlipped = false;
          newCards[secondIndex].isFlipped = false;
          setCards(newCards);
          setFlippedCards([]);
          setIsChecking(false);
        }, 1200);
      }
    }
  };

  // Touch handlers for swipe navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onNext) {
      onNext();
    } else if (isRightSwipe && onPrevious) {
      onPrevious();
    }
  };

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(10px, 2vh, 20px)',
      paddingTop: 'clamp(60px, 10vh, 100px)',
      boxSizing: 'border-box',
      overflowY: 'auto',
      backgroundColor: 'transparent',
    }}>
      {/* Celebration Animation */}
      {celebrating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {/* Confetti emojis */}
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                fontSize: '40px',
                animation: `confetti-fall-${i % 5} 3s ease-out forwards`,
                left: `${Math.random() * 100}%`,
                top: '-50px',
                opacity: 0,
              }}
            >
              {['🎉', '🎊', '⭐', '✨', '🌟'][i % 5]}
            </div>
          ))}
          
          {/* Success message */}
          <div style={{
            fontSize: 'clamp(40px, 10vw, 80px)',
            fontWeight: 'bold',
            color: '#fff',
            textShadow: '0 0 20px rgba(255, 215, 0, 0.8), 0 0 40px rgba(255, 215, 0, 0.6)',
            animation: 'celebration-pulse 1s ease-in-out infinite',
          }}>
            🎉 ¡Excellent! 🎉
          </div>
        </div>
      )}

      {/* Game Board */}
      <div className="game-board" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: 'clamp(8px, 1.5vmin, 15px)',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '60vh',
        aspectRatio: '4/3',
        margin: '0 auto'
      }}>
        {cards.map((card, index) => (
          <div
            key={card.id}
            onClick={() => handleCardClick(index)}
            style={{
              perspective: '1000px',
              cursor: card.isMatched ? 'default' : 'pointer',
              width: '100%',
              height: '100%',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                transformStyle: 'preserve-3d',
                transition: 'transform 0.6s',
                transform: card.isFlipped || card.isMatched ? 'rotateY(180deg)' : 'rotateY(0)',
              }}
            >
              {/* Card Back */}
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  backgroundColor: 'rgba(100, 149, 237, 0.85)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 'clamp(10px, 2vmin, 20px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(30px, 6vmin, 60px)',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.3s',
                }}
                onMouseOver={(e) => {
                  if (!card.isMatched) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.3)';
                }}
              >
                ❓
              </div>

              {/* Card Front */}
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  backgroundColor: card.isMatched ? 'rgba(144, 238, 144, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 'clamp(10px, 2vmin, 20px)',
                  transform: 'rotateY(180deg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'clamp(8px, 2vmin, 15px)',
                  boxShadow: card.isMatched ? '0 8px 24px rgba(144, 238, 144, 0.6)' : '0 8px 16px rgba(0, 0, 0, 0.3)',
                  border: card.isMatched ? '3px solid rgba(34, 139, 34, 0.6)' : '3px solid rgba(100, 149, 237, 0.3)',
                  overflow: 'hidden',
                  animation: card.isMatched ? 'match-found 0.6s ease-out' : 'none',
                }}
              >
                <img
                  src={card.imageUrl}
                  alt="card"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 'clamp(6px, 1.5vmin, 12px)',
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        maxWidth: '800px',
        marginTop: '20px'
      }}>
        <button
          onClick={onPrevious}
          style={{
            padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
            fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
            backgroundColor: 'rgba(100,100,100,0.8)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}
        >
          ← Previous
        </button>

        <button
          onClick={initializeGame}
          style={{
            padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
            fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}
        >
          🔄 Restart Game
        </button>

        <button
          onClick={onNext}
          style={{
            padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
            fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
            backgroundColor: 'rgba(100,100,100,0.8)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            fontWeight: '600',
            whiteSpace: 'nowrap'
          }}
        >
          Next →
        </button>
      </div>

    </div>
  );
};

export default Play;
